import React, { useEffect } from "react";
import { ArrowLeft, Shield } from "lucide-react";

export default function PrivacyPolicy({ onBack }) {
  useEffect(() => {
    document.title = "Privacy Policy — HEXA OS";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "HEXA OS Privacy Policy — how we collect, use and protect your data on hexawebos.com.");

    // Inject WebPage + BreadcrumbList JSON-LD for rich search snippets
    let script = document.getElementById("hexa-privacy-jsonld");
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "hexa-privacy-jsonld";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Privacy Policy — HEXA OS",
        "url": "https://hexawebos.com/privacy",
        "description": "HEXA OS Privacy Policy — how we collect, use and protect your data.",
        "inLanguage": "en",
        "isPartOf": { "@type": "WebSite", "name": "HEXA OS", "url": "https://hexawebos.com" },
        "publisher": { "@type": "Organization", "name": "HEXA", "url": "https://hexawebos.com" },
        "dateModified": "2026-02-08"
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home",           "item": "https://hexawebos.com/" },
          { "@type": "ListItem", "position": 2, "name": "Privacy Policy", "item": "https://hexawebos.com/privacy" }
        ]
      }
    ]);
    return () => {
      const el = document.getElementById("hexa-privacy-jsonld");
      if (el) el.remove();
    };
  }, []);

  return (
    <div
      className="text-white"
      style={{
        position: "fixed",
        inset: 0,
        overflowY: "auto",
        background: "#070b1a",
        fontFamily: "'Inter', system-ui",
        WebkitOverflowScrolling: "touch",
      }}
      data-testid="page-privacy"
    >
      <div className="max-w-3xl mx-auto px-6 py-14">
        <button
          data-testid="privacy-back"
          onClick={onBack}
          className="mono text-[10px] uppercase tracking-[0.28em] text-white/60 hover:text-white flex items-center gap-1 mb-8"
        >
          <ArrowLeft size={12} /> Back to HEXA
        </button>

        <div className="flex items-center gap-3 mb-2">
          <Shield size={22} className="text-[#a78bfa]" />
          <h1 className="text-3xl font-bold">Privacy Policy</h1>
        </div>
        <div className="mono text-[10px] uppercase tracking-[0.28em] text-white/45 mb-8">
          Effective: 8 July 2026 · Last updated: 8 July 2026
        </div>

        <div className="space-y-6 text-[14.5px] leading-relaxed text-white/85">
          <p>
            HEXA (&ldquo;HEXA&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;) operates the HEXA OS
            web application (<a className="text-[#a78bfa] underline" href="https://hexawebos.com">hexawebos.com</a>).
            This Privacy Policy explains what information we collect, why we collect it,
            and how we protect it.
          </p>

          <Section title="1. Information We Collect">
            <ul className="list-disc pl-5 space-y-1.5">
              <li><b>Account data</b>: name, email address (@hexawebos.com or your own), password (hashed), designation, company name & address, profile photo.</li>
              <li><b>Usage data</b>: features used, workspace activity, referral coins, timestamps.</li>
              <li><b>Content</b>: emails, documents, spreadsheets, notes, tasks, meetings, files you create in HEXA.</li>
              <li><b>External mailboxes</b>: if you connect Gmail / Yahoo / Outlook / custom IMAP, we store the email address + an encrypted (Fernet) copy of the app password on our servers.</li>
              <li><b>Cookies & local storage</b>: session tokens, theme preference, offline queue, referral code.</li>
            </ul>
          </Section>

          <Section title="2. How We Use Your Data">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Provide, operate and improve HEXA OS features (mail, chat, docs, meetings, etc.).</li>
              <li>Authenticate you and secure your account.</li>
              <li>Route email through your connected external accounts (real SMTP/IMAP).</li>
              <li>Send transactional notifications (welcome, referrals, security alerts).</li>
              <li>Detect abuse, fraud and violations of our Terms.</li>
              <li>Comply with legal obligations under the Indian IT Act 2000, GDPR, CCPA and other applicable laws.</li>
            </ul>
          </Section>

          <Section title="3. Data We DO NOT Share">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>We do NOT sell your personal data to advertisers or third parties.</li>
              <li>Your document / email content is NEVER used to train third-party AI models.</li>
              <li>AI features run via Emergent LLM providers (Anthropic Claude, OpenAI, Google Gemini) under a Data Processing Agreement that prohibits training on your data.</li>
            </ul>
          </Section>

          <Section title="4. Security">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>All traffic over HTTPS/TLS 1.3.</li>
              <li>Passwords hashed with bcrypt (never stored in plain text).</li>
              <li>External-mail app passwords encrypted at rest with Fernet symmetric encryption.</li>
              <li>Isolated per-workspace MongoDB collections; RBAC enforced on every API request.</li>
              <li>Regular dependency updates and vulnerability scans.</li>
            </ul>
          </Section>

          <Section title="5. Your Rights">
            You may access, correct, export or delete your data at any time from
            <b> My Profile</b> in the app or by writing to <a className="text-[#a78bfa] underline" href="mailto:privacy@hexawebos.com">privacy@hexawebos.com</a>.
            We respond to all verified requests within 30 days.
          </Section>

          <Section title="6. Data Retention">
            We retain account data for as long as your account is active plus 90
            days after deletion (for backup recovery). Uploaded files and mail are
            deleted from our systems within 30 days of account closure. Backups
            are purged on a rolling 90-day cycle.
          </Section>

          <Section title="7. Children">
            HEXA OS is not intended for users under 13. If we learn we&apos;ve
            collected data from a child under 13, we delete it immediately.
          </Section>

          <Section title="8. Changes to this Policy">
            We will notify all users by in-app banner and email at least 14 days
            before any material change to this policy.
          </Section>

          <Section title="9. Contact">
            <div>Email: <a className="text-[#a78bfa] underline" href="mailto:privacy@hexawebos.com">privacy@hexawebos.com</a></div>
            <div>Company: HEXA · India</div>
            <div>Website: <a className="text-[#a78bfa] underline" href="https://hexawebos.com">hexawebos.com</a></div>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-lg font-semibold text-white mb-2">{title}</h2>
      <div className="text-white/80">{children}</div>
    </section>
  );
}
